import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import {
  ArrowLeft, Home, MapPin, Phone, Mail, User, Pencil, Loader2, Plus, Upload,
  FileText, Clock, ChevronRight, Download, HardHat,
} from "lucide-react";
import { fmtMoney, STATUS_LABEL, STATUS_TONE, shortId } from "../workspace/util";
import { PlotFormDialog } from "./Plots";

const TABS = [
  { key: "overview",  label: "Overview" },
  { key: "jobs",      label: "Jobs" },
  { key: "documents", label: "Documents" },
  { key: "timeline",  label: "Timeline" },
];

export default function PlotRecord() {
  const { plotId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [plot, setPlot] = useState(null);
  const [docs, setDocs] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    const { data } = await adminApi.get(`/assets/plots/${plotId}`);
    setPlot(data);
    const [dc, tl] = await Promise.all([
      adminApi.get(`/assets/plots/${plotId}/documents`),
      adminApi.get(`/assets/plots/${plotId}/timeline`),
    ]);
    setDocs(dc.data.items || []);
    setTimeline(tl.data.items || []);
  }, [plotId]);

  useEffect(() => { load().catch((e) => toast.error(e.response?.data?.detail || "Plot not found")); }, [load]);

  if (!plot) return <div className="text-[#475569] flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  const photoSrc = plot.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${plot.photo_url}` : null;
  const openJobs = plot.open_jobs || [];
  const closedJobs = plot.closed_jobs || [];

  // Pre-fill a new support case from this plot's identity.
  const raiseJob = () => {
    const params = new URLSearchParams({
      park_name: plot.park?.name || "",
      plot_number: plot.plot_number || "",
      full_name: plot.owner_name || "",
      email: plot.owner_email || "",
      phone: plot.owner_phone || "",
    });
    navigate(`/adminpanel/raise-job?${params.toString()}`);
  };

  return (
    <div data-testid="plot-record-page" className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8 -mb-4 sm:-mb-6 lg:-mb-8">
      {/* HEADER */}
      <header className="bg-[#071A33] text-white px-4 sm:px-6 lg:px-8 py-6 border-b border-black/30">
        <button onClick={() => navigate("/adminpanel/assets/plots")} className="text-[12px] text-white/65 hover:text-white inline-flex items-center gap-1.5 mb-4" data-testid="plot-back">
          <ArrowLeft size={13}/> Plots
        </button>
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-32 h-24 rounded-lg overflow-hidden bg-white/[0.06] border border-white/10 shrink-0">
            {photoSrc ? <img src={photoSrc} className="w-full h-full object-cover" alt=""/> : <Home size={28} className="text-white/30 m-auto"/>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-semibold">
              <Link to={`/adminpanel/assets/parks/${plot.park?.id}`} className="hover:underline">{plot.park?.name || "Park"}</Link>
            </p>
            <h1 className="font-display text-[34px] lg:text-[40px] leading-tight tracking-tight text-white mt-1" data-testid="plot-title">Plot {plot.plot_number}</h1>
            {plot.owner_name && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[13px] text-white/75" data-testid="plot-owner-line">
                <span className="flex items-center gap-1.5"><User size={13} className="text-[#B88A2A]"/>{plot.owner_name}</span>
                {plot.owner_phone && <span className="flex items-center gap-1.5"><Phone size={12}/>{plot.owner_phone}</span>}
                {plot.owner_email && <span className="flex items-center gap-1.5"><Mail size={12}/>{plot.owner_email}</span>}
              </div>
            )}
            <div className="mt-4 grid grid-cols-3 gap-3 max-w-md">
              <HeaderStat label="Open jobs" value={openJobs.length} tone={openJobs.length > 0 ? "amber" : "muted"}/>
              <HeaderStat label="Total spend" value={fmtMoney(plot.total_spend)} muted/>
              <HeaderStat label="Status" value={plot.occupied ? "Occupied" : "Vacant"} muted/>
            </div>
          </div>
          <div className="flex flex-col gap-2 self-start">
            <button onClick={raiseJob} className="text-[12px] bg-[#B88A2A] text-white hover:bg-[#a07920] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md font-semibold" data-testid="plot-raise-job-btn">
              <Plus size={13}/> Raise new job
            </button>
            <button onClick={() => setEditing(true)} className="text-[12px] bg-white text-[#071A33] hover:bg-[#FFE7B6] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md font-semibold" data-testid="plot-edit-btn">
              <Pencil size={13}/> Edit plot
            </button>
          </div>
        </div>
      </header>

      <nav className="bg-[#F7F4ED]/60 border-b border-[#E2DCD0] px-4 sm:px-6 lg:px-8 flex overflow-x-auto">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-[13px] px-5 py-3.5 border-b-2 transition whitespace-nowrap ${active ? "border-[#064E2E] text-[#071A33] font-semibold bg-white" : "border-transparent text-[#475569] hover:text-[#071A33]"}`}
              data-testid={`plot-tab-${t.key}`}>
              {t.label}
              {t.key === "jobs" && (openJobs.length + closedJobs.length) > 0 && <span className="ml-2 text-[10px] bg-[#B88A2A] text-white rounded-full px-1.5 py-0.5">{openJobs.length + closedJobs.length}</span>}
            </button>
          );
        })}
      </nav>

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {tab === "overview"  && <Overview plot={plot}/>}
        {tab === "jobs"      && <Jobs plot={plot} openJobs={openJobs} closedJobs={closedJobs} onRaise={raiseJob}/>}
        {tab === "documents" && <Docs plotId={plotId} docs={docs} onReload={load}/>}
        {tab === "timeline"  && <Timeline items={timeline}/>}
      </div>

      <PlotFormDialog plot={editing ? plot : null} parks={null} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }}/>
    </div>
  );
}

function HeaderStat({ label, value, tone, muted }) {
  return (
    <div className={`rounded-md px-3 py-2 border ${tone === "amber" ? "bg-[#B88A2A]/15 border-[#B88A2A]/30" : "bg-white/[0.05] border-white/10"}`}>
      <p className="text-[10px] tracking-[0.16em] uppercase text-white/45">{label}</p>
      <p className={`text-xl font-display tabular-nums mt-0.5 ${muted ? "text-white/80" : "text-white"}`}>{value ?? "—"}</p>
    </div>
  );
}

function Overview({ plot }) {
  const rows = [
    ["Plot number", plot.plot_number],
    ["Park", plot.park?.name || "—"],
    ["Owner name", plot.owner_name || "—"],
    ["Owner phone", plot.owner_phone || "—"],
    ["Owner email", plot.owner_email || "—"],
    ["Manufacturer", plot.manufacturer || "—"],
    ["Model", plot.model || "—"],
    ["Year", plot.year || "—"],
    ["Occupancy", plot.occupied ? "Occupied" : "Vacant"],
    ["Status", plot.status || "ACTIVE"],
  ];
  return (
    <div className="admin-panel p-5" data-testid="plot-overview">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="border-b border-[#E2DCD0] pb-2">
            <dt className="text-[10px] tracking-[0.18em] uppercase text-[#475569]">{k}</dt>
            <dd className="text-[#071A33] mt-0.5">{v ?? "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Jobs({ plot, openJobs, closedJobs, onRaise }) {
  return (
    <div className="space-y-5" data-testid="plot-jobs-tab">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Open jobs" value={openJobs.length} tone={openJobs.length > 0 ? "amber" : "neutral"}/>
        <StatCard label="Closed jobs" value={closedJobs.length}/>
        <StatCard label="Total spend" value={fmtMoney(plot.total_spend)} tone="positive"/>
      </div>

      {plot.contractors?.length > 0 && (
        <div className="admin-panel p-4">
          <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569] mb-2">Assigned contractors</p>
          <div className="flex flex-wrap gap-2">
            {plot.contractors.map((c) => (
              <span key={c} className="text-[12px] bg-[#0F766E]/10 text-[#0F766E] px-2.5 py-1 rounded-full inline-flex items-center gap-1.5"><HardHat size={11}/>{c}</span>
            ))}
          </div>
        </div>
      )}

      <Section title={`Open jobs (${openJobs.length})`} action={
        <button onClick={onRaise} className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#B88A2A] text-white hover:bg-[#a07920]" data-testid="plot-jobs-raise-btn">
          <Plus size={12}/> Raise new job
        </button>
      }>
        {openJobs.length === 0 ? <Empty text="No open jobs."/> : <JobsTable jobs={openJobs}/>}
      </Section>

      <Section title={`Closed jobs (${closedJobs.length})`}>
        {closedJobs.length === 0 ? <Empty text="No closed jobs yet."/> : <JobsTable jobs={closedJobs}/>}
      </Section>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const cls = tone === "amber" ? "bg-[#B88A2A]/10 border-[#B88A2A]/30"
            : tone === "positive" ? "bg-[#064E2E]/5 border-[#064E2E]/30"
            : "bg-white border-[#E2DCD0]";
  return (
    <div className={`border rounded-lg p-4 ${cls}`}>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569]">{label}</p>
      <p className="font-display text-2xl text-[#071A33] tabular-nums mt-1">{value}</p>
    </div>
  );
}

function JobsTable({ jobs }) {
  return (
    <div className="admin-panel overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] tracking-[0.18em] uppercase text-[#475569] bg-[#F7F4ED]/60">
          <tr className="text-left">
            <th className="px-4 py-2">Job ID</th>
            <th className="px-4 py-2">Type</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Contractor</th>
            <th className="px-4 py-2 text-right">Value</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E2DCD0]">
          {jobs.map((j) => {
            const tone = STATUS_TONE[(j.status || "NEW").toUpperCase()] || STATUS_TONE.NEW;
            return (
              <tr key={j.id} className="hover:bg-[#F7F4ED]/40">
                <td className="px-4 py-2 font-mono text-[#0F766E]"><Link to={`/adminpanel/workspace/${j.id}`} className="hover:underline">{shortId(j.id)}</Link></td>
                <td className="px-4 py-2 text-[#475569]">{(j.case_type || "—").replace(/_/g, " ")}</td>
                <td className="px-4 py-2"><span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded ${tone}`}>{STATUS_LABEL[j.status] || j.status}</span></td>
                <td className="px-4 py-2 text-[#475569]">{j.contractor_name || "—"}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(j.client_price_ex_vat ?? j.quote_value ?? 0)}</td>
                <td className="px-4 py-2 text-right"><Link to={`/adminpanel/workspace/${j.id}`} className="text-[#0F766E] text-[12px] inline-flex items-center gap-1 hover:underline">Open <ChevronRight size={11}/></Link></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Docs({ plotId, docs, onReload }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("plot_id", plotId);
      fd.append("file", file);
      await adminApi.post("/assets/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onReload();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="admin-panel p-5" data-testid="plot-docs-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#475569]">{docs.length} document{docs.length === 1 ? "" : "s"}</p>
        <label className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#064E2E] text-white hover:bg-[#0a5b55] cursor-pointer">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} Upload document
          <input type="file" className="hidden" onChange={upload}/>
        </label>
      </div>
      {docs.length === 0 ? <Empty text="No documents uploaded."/> : (
        <ul className="divide-y divide-[#E2DCD0]">
          {docs.map((d) => (
            <li key={d.id} className="py-2 flex items-center justify-between text-sm">
              <span className="flex items-center gap-2"><FileText size={13} className="text-[#475569]"/>{d.filename}<span className="text-[10px] text-[#475569]">· {Math.round((d.size || 0) / 1024)} KB</span></span>
              <a href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="text-[#0F766E] hover:underline inline-flex items-center gap-1"><Download size={12}/> Open</a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Timeline({ items }) {
  if (!items.length) return <Empty text="No activity yet."/>;
  return (
    <ul className="admin-panel p-5 space-y-3" data-testid="plot-timeline-tab">
      {items.map((a) => (
        <li key={a.id} className="flex items-start gap-3">
          <Clock size={13} className="text-[#475569] mt-0.5 shrink-0"/>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[#071A33]">{a.summary}</p>
            <p className="text-[10px] text-[#475569] mt-0.5">{new Date(a.at).toLocaleString("en-GB")}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Section({ title, action, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-3">
        <h4 className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-medium">{title}</h4>
        {action}
      </div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-center text-[#475569] italic py-6">{text}</p>;
}
